/**
 * Single source of truth for PermitStatus categories and their per-category
 * metadata. All `Record<PermitStatus, T>` lookups, filter arrays, and Zod
 * schemas across the codebase derive from the const below — do not re-declare
 * per-category data in individual files.
 *
 * Adding a new category:
 *   1. Add the enum value to `packages/api/prisma/schema.prisma` and run `bun push`.
 *   2. Add an entry to `CATEGORY_METADATA` here.
 *   3. Optionally add a default-named status to `DEFAULT_STATUSES` in
 *      `packages/api/src/shared/seed-org-workflow-statuses.ts` so new orgs
 *      get a seeded column in the new category.
 *   4. Handle the new category in any `switch(status)` blocks that have
 *      distinct semantics per category (e.g. `compute-sprint-counts.ts`).
 *
 * TypeScript's exhaustive `Record<PermitStatus, T>` checks and the
 * `satisfies` constraint below will guide step 2 — forgetting a category
 * here is a compile error.
 */

/** Canonical board order: left-to-right on the Kanban view and Settings page. */
export const CATEGORY_ORDER = [
	'INBOX',
	'BACKLOG',
	'IN_PROGRESS',
	'DONE',
] as const

export type PermitStatus = (typeof CATEGORY_ORDER)[number]

export type CategoryMeta = {
	/** Display label shown in UI column headers, dropdowns, and emails. */
	label: string
	/** Numeric sort weight — drives board column order and Settings section order. */
	sortOrder: number
	/** Settings-page description under each category section. */
	description: string
	/** Placeholder name used when a user adds a new status in this category via Settings. */
	defaultName: string
	/** Terminal = permit is out of pipeline. Non-terminal categories appear in "active" filters. */
	isTerminal: boolean
	/** Hex color used when seeding the default-named status for this category. */
	seedColor: string
	/** Email status-changed template color pair (bg + text). */
	emailColor: {bg: string; text: string}
}

export const CATEGORY_METADATA = {
	INBOX: {
		label: 'Inbox',
		sortOrder: 0,
		description:
			'Fresh permits that have just arrived and have not been triaged yet — new permits land here by default',
		defaultName: 'New Inbox Status',
		isTerminal: false,
		seedColor: '#8b5cf6',
		emailColor: {bg: '#ede9fe', text: '#5b21b6'},
	},
	BACKLOG: {
		label: 'Backlog',
		sortOrder: 1,
		description: 'Triaged permits that need attention',
		defaultName: 'New Backlog Status',
		isTerminal: false,
		seedColor: '#6b7280',
		emailColor: {bg: '#e2e8f0', text: '#4a5568'},
	},
	IN_PROGRESS: {
		label: 'In Progress',
		sortOrder: 2,
		description: 'Permits being actively worked on',
		defaultName: 'New Active Status',
		isTerminal: false,
		seedColor: '#3b82f6',
		emailColor: {bg: '#bee3f8', text: '#2b6cb0'},
	},
	DONE: {
		label: 'Done',
		sortOrder: 3,
		description: 'Completed or closed permits',
		defaultName: 'New Done Status',
		isTerminal: true,
		seedColor: '#6b7280',
		emailColor: {bg: '#c6f6d5', text: '#276749'},
	},
} as const satisfies Record<PermitStatus, CategoryMeta>

/**
 * Non-terminal categories — use in `status: { in: ACTIVE_CATEGORIES }`
 * filters to match permits that haven't been closed yet.
 */
export const ACTIVE_CATEGORIES: PermitStatus[] = CATEGORY_ORDER.filter(
	(c) => !CATEGORY_METADATA[c].isTerminal,
)

/** Terminal categories — currently just `DONE`. */
export const TERMINAL_CATEGORIES: PermitStatus[] = CATEGORY_ORDER.filter(
	(c) => CATEGORY_METADATA[c].isTerminal,
)
